/**
 * LocalizedNumberDisplay - Display numbers with user's preferred format
 */

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { useUserPreferences } from '../context';
import { LtrToken } from './common/LtrToken';

export type NumberFormatOverride = 'comma_dot' | 'dot_comma';

export interface LocalizedNumberDisplayProps {
  value: number | null | undefined;
  decimals?: number;
  /**
   * Sign rendering, passed straight to `Intl.NumberFormat`. Use `'exceptZero'`
   * for signed deltas (a ledger movement) so a credit reads `+1.7` — hand-rolling
   * the `+` skips the locale's decimal separator and sign placement.
   */
  signDisplay?: Intl.NumberFormatOptions['signDisplay'];
  /** Override the number format instead of using user preference */
  overrideNumberFormat?: NumberFormatOverride;
  showTooltip?: boolean;
  emptyText?: string;
  className?: string;
}

/**
 * Format a number with a specific number format
 */
function formatNumberWithOverride(
  value: number,
  numberFormat: NumberFormatOverride,
  options?: Intl.NumberFormatOptions
): string {
  const locale = numberFormat === 'comma_dot' ? 'en-US' : 'de-DE';
  return new Intl.NumberFormat(locale, options).format(value);
}

export function LocalizedNumberDisplay({
  value,
  decimals,
  signDisplay,
  overrideNumberFormat,
  showTooltip = false,
  emptyText = '-',
  className,
}: LocalizedNumberDisplayProps) {
  const { formatNumber } = useUserPreferences();

  if (value === null || value === undefined) {
    return <span className={className}>{emptyText}</span>;
  }

  const options: Intl.NumberFormatOptions = {};
  if (decimals !== undefined) {
    options.minimumFractionDigits = decimals;
    options.maximumFractionDigits = decimals;
  }
  if (signDisplay !== undefined) {
    options.signDisplay = signDisplay;
  }

  const formattedNumber = overrideNumberFormat
    ? formatNumberWithOverride(value, overrideNumberFormat, options)
    : formatNumber(value, options);

  const content = <LtrToken className={className}>{formattedNumber}</LtrToken>;

  if (showTooltip) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{content}</TooltipTrigger>
          <TooltipContent>
            <p>{value.toString()}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return content;
}
